/**
 * WebRTC Control — toggle WebRTC to prevent IP leaks
 *
 * Declares a single web-accessible-resource group matched to http(s) origins with no
 * `use_dynamic_url`, exposing three page-context scripts under
 * `data/content_script/page_context/`. All three resolve from any page at a fixed URL.
 *
 * `media_devices.js` is the primary signal — a vendor-pathed script served only when
 * the extension is installed, so fetching it is conclusive. The other two page-context
 * scripts corroborate.
 *
 * Manifest verified against version 0.3.5.
 */

import { probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'fjkmabmdepjfammlpliljpnbhleegehm';

export const webrtcControl: ExtensionDefinition = {
  id: ID,
  name: 'WebRTC Control',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 200_000,
  notes:
    'Three static WAR on http(s) origins with no dynamic URL. Fetching the ' +
    'media_devices.js page-context script is conclusive; the other two corroborate.',
  signals: [
    {
      id: 'war-media-devices-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch data/content_script/page_context/media_devices.js — a page-context script ' +
        'exposed to every http(s) origin at a fixed URL, served only when installed.',
      weight: 1,
      run: () => probeFetch(ID, 'data/content_script/page_context/media_devices.js'),
    },
    {
      id: 'war-support-detection-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch data/content_script/page_context/support_detection.js — a second page-context script.',
      weight: 0.5,
      run: () => probeFetch(ID, 'data/content_script/page_context/support_detection.js'),
    },
    {
      id: 'war-additional-objects-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch data/content_script/page_context/additional_objects.js — a third independent path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'data/content_script/page_context/additional_objects.js'),
    },
  ],
};
